import {
  Button,
  Input,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "@heroui/react";
import { Controller, useForm } from "react-hook-form";
import { TempAgreement } from "@/api/temp-agreements/temp-agreements";
import { Requisite } from "@/api/requisites/requisites";
import { useEffect, useState } from "react";
import { api } from "@/api";

interface BankDetailsFormProps {
  defaults: Partial<TempAgreement>;
  onSubmit: (data: Partial<TempAgreement>) => void;
  renterId?: number;
  onRequisiteSelect?: (requisiteId: number) => void;
}

export function BankDetailsForm({
  defaults,
  onSubmit,
  renterId,
  onRequisiteSelect,
}: BankDetailsFormProps) {
  const [existingRequisites, setExistingRequisites] = useState<Requisite[]>([]);
  const [showRequisiteModal, setShowRequisiteModal] = useState(false);

  const form = useForm<Partial<TempAgreement>>({
    defaultValues: defaults,
  });

  const handleSubmit = (e: Partial<TempAgreement>) => {
    onSubmit(e);
  };

  useEffect(() => {
    if (renterId) {
      api.requisites
        .listByRenter(renterId)
        .then((response) => {
          if (response.data && response.data.length > 0) {
            setExistingRequisites(response.data);
            setShowRequisiteModal(true);
          }
        })
        .catch(() => {});
    }
  }, [renterId]);

  const handleUseExistingRequisite = (requisite: Requisite) => {
    if (onRequisiteSelect) {
      onRequisiteSelect(requisite.id);

      form.setValue("bank_name", requisite.bank_name);
      form.setValue("kpp", requisite.kpp);
      form.setValue("bik", requisite.bik);
      form.setValue("bank_account", requisite.bank_account);
      form.setValue("correspondent_account", requisite.correspondent_account);
      form.setValue("requisite_id", requisite.id);
    }
    setShowRequisiteModal(false);

    setTimeout(() => {
      handleSubmit(form.getValues());
    }, 100);
  };

  const handleCreateNewRequisite = () => {
    setShowRequisiteModal(false);
  };

  return (
    <>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="w-full max-w-2xl space-y-4 p-6 bg-background rounded-xl border-2 border-foreground-200 dark:border-foreground-100"
      >
        <Controller
          control={form.control}
          name="bank_name"
          rules={{ required: true }}
          render={({ field }) => (
            <Input
              label="Название банка"
              variant="bordered"
              isRequired
              isInvalid={!!form.formState.errors.bank_name}
              errorMessage={form.formState.errors.bank_name?.message as string}
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="kpp"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="КПП"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.kpp}
                errorMessage={form.formState.errors.kpp?.message as string}
                value={field.value?.toString() || ""}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={form.control}
            name="bik"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="БИК"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.bik}
                errorMessage={form.formState.errors.bik?.message as string}
                value={field.value?.toString() || ""}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <Controller
            control={form.control}
            name="bank_account"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Расчётный счёт"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.bank_account}
                errorMessage={
                  form.formState.errors.bank_account?.message as string
                }
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
          <Controller
            control={form.control}
            name="correspondent_account"
            rules={{ required: true }}
            render={({ field }) => (
              <Input
                label="Корреспондентский счёт"
                variant="bordered"
                isRequired
                isInvalid={!!form.formState.errors.correspondent_account}
                errorMessage={
                  form.formState.errors.correspondent_account?.message as string
                }
                value={field.value}
                onChange={field.onChange}
              />
            )}
          />
        </div>
        <Button type="submit" variant="bordered" color="success">
          Подтвердить
        </Button>
      </form>

      <Modal
        isOpen={showRequisiteModal}
        onOpenChange={setShowRequisiteModal}
        scrollBehavior="outside"
      >
        <ModalContent>
          <ModalHeader>Выберите вариант</ModalHeader>
          <ModalBody>
            <p>
              В системе уже зарегистрированы реквизиты для этой компании. Хотите
              использовать существующие или создать новые?
            </p>

            {existingRequisites.length > 0 && (
              <div className="mt-4 space-y-3">
                <p className="font-semibold">Существующие реквизиты:</p>
                {existingRequisites.map((requisite) => (
                  <div
                    key={requisite.id}
                    className="p-4 bg-gray-100 dark:bg-gray-800 rounded-lg cursor-pointer hover:bg-gray-200 dark:hover:bg-gray-700"
                    onClick={() => handleUseExistingRequisite(requisite)}
                  >
                    <p>
                      <strong>Название:</strong> {requisite.display_name}
                    </p>
                    <p>
                      <strong>Банк:</strong> {requisite.bank_name}
                    </p>
                    <p>
                      <strong>ИНН:</strong> {requisite.inn}
                    </p>
                    <p>
                      <strong>КПП:</strong> {requisite.kpp}
                    </p>
                    <p>
                      <strong>БИК:</strong> {requisite.bik}
                    </p>
                    <p>
                      <strong>Расчётный счёт:</strong> {requisite.bank_account}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              variant="light"
              onPress={handleCreateNewRequisite}
            >
              Создать новые
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}
